import { Navigate, Route, Routes } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../api/client";
import type { Me } from "../../api/types";
import { DataTable } from "../../components/DataTable";
import { LinkTabs, PageHeader, Panel } from "../../components/Layout";
import { EmptyState, QueryState, SkeletonBlock } from "../../components/States";
import { dateTime, num } from "../../lib/format";
import { UsersTab } from "./UsersTab";
import { CataloguesTab } from "./CataloguesTab";
import { DecisionTab } from "./DecisionTab";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "../../components/Feedback";

type Row = Record<string, unknown>;

function SimpleTable({ rows, cols, caption, empty }: { rows: Row[]; cols: [string, string][]; caption: string; empty: string }) {
  return (
    <DataTable<Row>
      rows={rows}
      rowKey={(r) => cols.map(([k]) => String(r[k] ?? "")).join("|")}
      caption={caption}
      empty={<EmptyState title={empty} />}
      columns={cols.map(([k, label]) => ({
        key: k, label, sort: (r: Row) => (r[k] as string | number | null) ?? null,
        render: (r: Row) => {
          const v = r[k];
          if (v === null || v === undefined || v === "") return <span className="muted">—</span>;
          if (k === "active" || k === "ai_enabled" || k === "model_ready" || k === "is_active") return Number(v) ? "Yes" : "No";
          return String(v);
        },
      }))}
    />
  );
}

function ReferenceTab() {
  const q = useQuery({ queryKey: ["surveys", "readiness"], queryFn: () => api<{ checks: { name: string; ok: boolean; detail: string }[] }>("/api/surveys/readiness") });
  return (
    <Panel title="Reference data" id="ad-ref">
      <div className="stack">
        <p>GPS points are matched to the KRB road network and RICS 2023 segments to give each image a road and a chainage.</p>
        <QueryState query={q} skeleton={<SkeletonBlock rows={3} />}>
          {(d) => (
            <ul className="check-list" style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {d.checks.map((c) => (
                <li key={c.name} className="check-item"><span className={c.ok ? "ok" : "no"} aria-hidden="true">{c.ok ? "✓" : "✕"}</span><span><b>{c.name}</b>{c.ok ? " — installed" : " — not installed"}<small className="muted" style={{ display: "block" }}>{c.detail}</small></span></li>
              ))}
            </ul>
          )}
        </QueryState>
        <p className="notice">Importing RICS and KRB files still runs in the field console until it moves here in a later phase. Replacing reference data will ask for your password again.</p>
      </div>
    </Panel>
  );
}

function SystemTab() {
  const q = useQuery({ queryKey: ["admin", "system"], queryFn: () => api<{ sync: Row[]; ai_runs: Row[]; frames: Row; imports: Row[]; audit: Row[]; processing: Row[] }>("/api/admin/system"), refetchInterval: 60_000 });
  const qc = useQueryClient();
  const toast = useToast();
  const requeue = useMutation({
    mutationFn: () => api<{ rdd: number; assets: number; privacy: number }>("/api/admin/processing/requeue", { method: "POST" }),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: ["admin", "system"] }); toast({ text: `Queued ${r.rdd} image(s) for road-damage AI, ${r.assets} for asset AI and ${r.privacy} for privacy blur.` }); },
  });
  return (
    <QueryState query={q}>
      {(d) => (
        <>
          <div className="kpis">
            <div className="kpi"><span className="kpi-label">Images</span><span className="kpi-value">{num(Number(d.frames.total))}</span><span className="kpi-note">{num(Number(d.frames.privacy_done))} privacy-blurred · {num(Number(d.frames.excluded))} removed</span></div>
            <div className="kpi"><span className="kpi-label">Last data import</span><span className="kpi-value" style={{ fontSize: 20 }}>{d.imports[0] ? dateTime(String(d.imports[0].finished_at)) : "—"}</span><span className="kpi-note">{d.imports[0] ? `${num(Number(d.imports[0].rows_copied))} rows from the field database` : "Never"}</span></div>
          </div>
          <Panel title="Image processing" id="sys-proc" flush actions={<button className="btn btn-sm" disabled={requeue.isPending} onClick={() => requeue.mutate()}>Retry failed and skipped</button>}>
            <SimpleTable rows={d.processing} caption="Image processing" empty="No images" cols={[["step", "Step"], ["status", "Status"], ["n", "Images"]]} />
            <p className="muted" style={{ fontSize: 13, padding: "8px 16px" }}>PENDING images are processed by the worker (<span className="mono">python -m app.workers.processor</span>). Viewers only see images once privacy blur is DONE.</p>
          </Panel>
          <div className="grid grid-2">
            <Panel title="Sync from phones" id="sys-sync" flush><SimpleTable rows={d.sync} caption="Sync" empty="Nothing synced yet" cols={[["event_type", "Event"], ["n", "Count"], ["last", "Last"]]} /></Panel>
            <Panel title="Recent activity" id="sys-audit" flush><SimpleTable rows={d.audit} caption="Activity" empty="No activity" cols={[["created_at", "When"], ["username", "User"], ["event", "Event"], ["target", "Target"]]} /></Panel>
          </div>
          <Panel title="AI analysis runs" id="sys-ai" flush>
            <SimpleTable rows={d.ai_runs} caption="AI runs" empty="No AI runs" cols={[["started_at", "Started"], ["pipeline_name", "Pipeline"], ["status", "Status"], ["frames_processed", "Frames"], ["detections_created", "Detections"], ["error_text", "Error"]]} />
          </Panel>
        </>
      )}
    </QueryState>
  );
}

export function AdminPage({ me }: { me: Me }) {
  return (
    <>
      <PageHeader title="Admin" subtitle="Users, road access, reference data and system health." />
      <LinkTabs label="Admin sections" tabs={[
        { to: "/admin/users", label: "Users & roles" },
        { to: "/admin/reference", label: "Reference data" },
        { to: "/admin/catalogues", label: "Catalogues" },
        { to: "/admin/decision-engine", label: "Decision engine" },
        { to: "/admin/system", label: "System health" },
      ]} />
      <Routes>
        <Route index element={<Navigate to="users" replace />} />
        <Route path="users" element={<UsersTab me={me} />} />
        <Route path="reference" element={<ReferenceTab />} />
        <Route path="catalogues" element={<CataloguesTab />} />
        <Route path="decision-engine" element={<DecisionTab />} />
        <Route path="system" element={<SystemTab />} />
      </Routes>
    </>
  );
}
